'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { NavButton } from '@/components/ui/nav-button';
import { CatalogMenu } from '@/components/ui/home/catalog-menu';
import { Breadcrumb } from '@/components/ui/home/breadcrumb';
import { motion } from 'framer-motion';
import { BreadcrumbItem } from '@/components/ui/layout/fixed-layout';

interface HeaderProps {
  onCatalogMenuToggle?: () => void;
  isCatalogMenuOpen?: boolean;
  onLeftMenuToggle?: () => void;
  isLeftMenuOpen?: boolean;
  title?: string;
  breadcrumbs?: BreadcrumbItem[];
  pageKey?: string;
  isMobileCheck?: (isMobile: boolean) => void;
}

interface CatalogMenuItemProps {
  item: { label: string; href: string };
  index: number;
  isCatalogMenuOpen: boolean;
  isLeftMenuOpen: boolean;
  isActive: boolean;
}

// Separate component for menu items to properly handle state
function CatalogMenuItem({ item, index, isCatalogMenuOpen, isLeftMenuOpen, isActive }: CatalogMenuItemProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Define the strobing animation variants
  const strobingVariants = {
    initial: { filter: isActive ? 'invert(100%)' : 'invert(0%)' },
    hover: {
      filter: ['invert(0%)', 'invert(100%)', 'invert(0%)', 'invert(100%)', 'invert(0%)'],
      transition: {
        duration: 0.6,
        times: [0, 0.25, 0.5, 0.75, 1],
        ease: 'easeInOut',
      },
    },
  };

  return (
    <motion.div
      className={`opacity-0 ${isCatalogMenuOpen || isLeftMenuOpen ? 'animate-fade-in' : ''} overflow-hidden`}
      style={{
        animationDelay: `${80 + index * 40}ms`,
        animationFillMode: 'forwards',
      }}
      initial="initial"
      animate={isHovered ? 'hover' : 'initial'}
      variants={strobingVariants}
    >
      <Link
        href={item.href}
        className="nav-button inline-block"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {item.label}
      </Link>
    </motion.div>
  );
}

export function Header({
  onCatalogMenuToggle,
  isCatalogMenuOpen = false,
  onLeftMenuToggle,
  isLeftMenuOpen = false,
  breadcrumbs,
}: HeaderProps) {
  const pathname = usePathname();

  const catalogMainItems = [
    { label: 'ABOUT', href: '/about' },
    { label: 'PRESS & MEDIA', href: '/press' },
    { label: 'GUILD', href: '/insights/guild' },
    { label: 'ANALYTICS', href: '/insights/analytics' },
    { label: 'LEADERBOARD', href: '/insights/leaderboard' },
    { label: 'FEED', href: '/updates' },
    //{ label: 'CONTACTS', href: '/about' },
    //{ label: 'CLAIM GUILD PROFILE', href: '/about' },
  ];

  // Catalog menu items - æ
  const catalogMenuItems = [
    { label: 'ALL ARTWORKS', href: '/catalog' },
    { label: 'COLLECTIONS', href: '/catalog/collections' },
    { label: 'ONE OF ONES', href: '/catalog/one-of-ones' },
    { label: 'INSTALLATIONS', href: '/catalog/installations' },
    // { label: 'PHYSICAL ARTWORKS', href: '/catalog/physicals' },
    // { label: 'IMMERSIVE', href: '/catalog/immersive' },
    { label: 'EXHIBITIONS', href: '/catalog/exhibitions' },
    // { label: 'COMMISSIONS', href: '/catalog/commissions' },
    // { label: 'WRITINGS', href: '/catalog/writings' },
  ];

  const checkActive = (items: { label: string; href: string }[], path: string) => {
    // pathname.startsWith('/catalog')
    // return items.some(item => item.href === path);
    return items.some(item => item.href.startsWith(path));
  };

  // Function to check if a path is active
  const isActive = (path: string) => {
    // For exact matches like /about
    if (path === pathname) return true;

    // For catalog items, check if the current path starts with the item path
    // but only if the item path is not just '/catalog' (to avoid matching all catalog subpages)
    if (path !== '/catalog' && pathname.startsWith(path)) return true;

    // Special case for /catalog
    if (path === '/catalog' && pathname === '/catalog') return true;

    return false;
  };

  // hidden md:block

  return (
    <div className="transition-all duration-300 ease-in-out bg-black main-header">
      {/* Main Header */}

      <header
        className="px-2 pt-2 pb-[7px] hidden lg:flex justify-between 
       items-center relative border-b border-transparent transition-all
       duration-300 ease-in-out
       "
      >
        <div className="flex space-x-1">
          <CatalogMenu
            onToggle={onLeftMenuToggle}
            isOpen={isLeftMenuOpen}
            isActive={checkActive(catalogMainItems, pathname)}
            label={'INSIGHTS'}
            isLeft={true}
          />
          {/* <Link href="/press" className="inline-flex z-50 relative" prefetch={false}>
            <NavButton className="pointer-events-auto" isActive={isActive('/press')}>
              PRESS
            </NavButton>
          </Link>
          <Link href="/about" className="inline-flex z-50 relative" prefetch={false}>
            <NavButton className="pointer-events-auto" isActive={isActive('/about')}>
              ABOUT
            </NavButton>
          </Link>
          <Link href="/insights" className="inline-flex z-50 relative" prefetch={false}>
            <NavButton className="pointer-events-auto" isActive={isActive('/insights')}>
              INSIGHTS
            </NavButton>
          </Link> */}
        </div>

        <div className="absolute left-1/2 transform -translate-x-1/2">
          <Breadcrumb breadcrumbs={breadcrumbs} />
        </div>

        <div className="flex space-x-1">
          {/* todo: <NavButton>GOODS</NavButton> */}
          {/* <Link href="/writings" className="inline-flex z-50 relative" prefetch={false}>
            <NavButton className="pointer-events-auto" isActive={isActive('/writings')}>
              WRITINGS
            </NavButton>
          </Link> */}
          <CatalogMenu
            onToggle={onCatalogMenuToggle}
            isOpen={isCatalogMenuOpen}
            isActive={checkActive(catalogMenuItems, pathname)}
            label="CATALOG RAISONNE"
          />
        </div>
      </header>

      {/* Left Menu */}
      <div
        className={`hidden lg:flex overflow-hidden transition-all duration-300 ease-in-out
           ${isLeftMenuOpen ? 'animate-slide-down' : ''}`}
        style={{
          maxHeight: isLeftMenuOpen ? '60px' : '0',
          opacity: isLeftMenuOpen ? '1' : '0',
          // marginTop: isLeftMenuOpen ? '2px' : '0'
        }}
      >
        <div className="flex justify-start px-2 pt-0 pb-0 w-full">
          <div className="flex space-x-2 overflow-x-auto overflow-y-hidden py-0 pr-2 ">
            {catalogMainItems.map((item, index) => {
              return (
                <CatalogMenuItem
                  key={index}
                  item={item}
                  index={index}
                  isCatalogMenuOpen={isCatalogMenuOpen}
                  isLeftMenuOpen={isLeftMenuOpen}
                  isActive={isActive(item.href)}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Menu */}
      <div
        className={`hidden lg:flex overflow-hidden transition-all duration-300 ease-in-out
           ${isCatalogMenuOpen ? 'animate-slide-down' : ''}`}
        style={{
          maxHeight: isCatalogMenuOpen ? '60px' : '0',
          opacity: isCatalogMenuOpen ? '1' : '0',
          // marginTop: isCatalogMenuOpen ? '2px' : '0'
        }}
      >
        <div className="flex justify-end px-2 pt-0 pb-0 w-full">
          <div className="flex space-x-2 overflow-x-auto overflow-y-hidden py-0 pl-2 ">
            {catalogMenuItems.map((item, index) => {
              return (
                <CatalogMenuItem
                  key={index}
                  item={item}
                  index={index}
                  isCatalogMenuOpen={isCatalogMenuOpen}
                  isLeftMenuOpen={isLeftMenuOpen}
                  isActive={isActive(item.href)}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <header className="px-2 pt-2 pb-0 flex lg:hidden justify-between items-center relative border-b border-transparent transition-all duration-300 ease-in-out ">
        <div className="pl-0">
          <Breadcrumb breadcrumbs={breadcrumbs} mobile={true} />
        </div>
        <div className="flex space-x-1">
          <CatalogMenu
            onToggle={onCatalogMenuToggle}
            isOpen={isCatalogMenuOpen}
            isActive={pathname.startsWith('/catalog')}
            isMobile={true}
            label="CATALOG RAISONNE"
          />
        </div>
      </header>

      {/* Integrated Catalog Menu - expands within navbar */}
      <div
        className={`flex lg:hidden flex-col overflow-hidden transition-all duration-300 ease-in-out ${isCatalogMenuOpen ? 'animate-slide-down' : ''}`}
        style={{
          maxHeight: isCatalogMenuOpen ? '270px' : '0',
          opacity: isCatalogMenuOpen ? '1' : '0',
          // marginTop: isCatalogMenuOpen ? '2px' : '0'
        }}
      >
        <div className="bg-[#101010] m-2 rounded-md">
          <div className="flex justify-between p-1 mt-1  flex-row w-full relative uppercase text-[11px] font-normal">
            <div className="flex flex-col items-start pl-2 flex-1 ">[ INSIGHTS ]</div>
            <div className="flex flex-col items-end pr-2 flex-1 ">[ CATALOG RASIONNE ]</div>
          </div>
          <div className="border-b border-white/20 m-1 mx-3" />
          <div className="flex justify-between p-1 pt-0 flex-row w-full">
            <div className="flex flex-col items-start pl-2 flex-1 ">
              {catalogMainItems.map((item, index) => {
                return (
                  <CatalogMenuItem
                    key={index}
                    item={item}
                    index={index}
                    isCatalogMenuOpen={isCatalogMenuOpen}
                    isLeftMenuOpen={isLeftMenuOpen}
                    isActive={isActive(item.href)}
                  />
                );
              })}
            </div>
            <div className="flex flex-col items-end pr-2 flex-1 ">
              {catalogMenuItems.map((item, index) => {
                return (
                  <CatalogMenuItem
                    key={index}
                    item={item}
                    index={index}
                    isCatalogMenuOpen={isCatalogMenuOpen}
                    isLeftMenuOpen={isLeftMenuOpen}
                    isActive={isActive(item.href)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
